/** Mirrors apps/server/src/modules/cadastros/marcas/cor-marca.enum.ts */

/**
 * The twelve colours the Macro map paints brands with, in the order the product owner
 * approved (the largest brand takes the first one). Red, amber and green are missing on
 * purpose: they are the farol's and cannot mean a brand.
 *
 * The tones themselves live in the theme (`src/app/globals.css`), with a dark step where
 * the colour needs one. Here there are only class names: a component never writes a hex.
 */
export const CORES_MARCA = [
  'BLUE',
  'ORANGE',
  'TEAL',
  'VIOLET',
  'MAGENTA',
  'SKY',
  'BROWN',
  'ORCHID',
  'LILAC',
  'PINK',
  'OLIVE',
  'LIME',
] as const;

export type TCorMarca = (typeof CORES_MARCA)[number];

export interface IClassesCorMarca {
  /** Solid fill of the band, the chip or the swatch. */
  fundo: string;
  /** Border matching the fill, so two OPs side by side stay apart. */
  borda: string;
  /** Ink that reads on top of the fill, decided by contrast in each theme. */
  texto: string;
  /** The same fill for an SVG shape: the slice of the Visão executiva's pie. */
  preenche: string;
}

/**
 * Written out one by one, never built by string interpolation: Tailwind reads class
 * names from the source text and would not see a name assembled at runtime.
 */
const CLASSES: Record<TCorMarca, IClassesCorMarca> = {
  BLUE: {
    fundo: 'bg-marca-blue',
    borda: 'border-marca-blue',
    texto: 'text-marca-blue-fg',
    preenche: 'fill-marca-blue',
  },
  ORANGE: {
    fundo: 'bg-marca-orange',
    borda: 'border-marca-orange',
    texto: 'text-marca-orange-fg',
    preenche: 'fill-marca-orange',
  },
  TEAL: {
    fundo: 'bg-marca-teal',
    borda: 'border-marca-teal',
    texto: 'text-marca-teal-fg',
    preenche: 'fill-marca-teal',
  },
  VIOLET: {
    fundo: 'bg-marca-violet',
    borda: 'border-marca-violet',
    texto: 'text-marca-violet-fg',
    preenche: 'fill-marca-violet',
  },
  MAGENTA: {
    fundo: 'bg-marca-magenta',
    borda: 'border-marca-magenta',
    texto: 'text-marca-magenta-fg',
    preenche: 'fill-marca-magenta',
  },
  SKY: { fundo: 'bg-marca-sky', borda: 'border-marca-sky', texto: 'text-marca-sky-fg', preenche: 'fill-marca-sky' },
  BROWN: {
    fundo: 'bg-marca-brown',
    borda: 'border-marca-brown',
    texto: 'text-marca-brown-fg',
    preenche: 'fill-marca-brown',
  },
  ORCHID: {
    fundo: 'bg-marca-orchid',
    borda: 'border-marca-orchid',
    texto: 'text-marca-orchid-fg',
    preenche: 'fill-marca-orchid',
  },
  LILAC: {
    fundo: 'bg-marca-lilac',
    borda: 'border-marca-lilac',
    texto: 'text-marca-lilac-fg',
    preenche: 'fill-marca-lilac',
  },
  PINK: {
    fundo: 'bg-marca-pink',
    borda: 'border-marca-pink',
    texto: 'text-marca-pink-fg',
    preenche: 'fill-marca-pink',
  },
  OLIVE: {
    fundo: 'bg-marca-olive',
    borda: 'border-marca-olive',
    texto: 'text-marca-olive-fg',
    preenche: 'fill-marca-olive',
  },
  LIME: {
    fundo: 'bg-marca-lime',
    borda: 'border-marca-lime',
    texto: 'text-marca-lime-fg',
    preenche: 'fill-marca-lime',
  },
};

/** A brand with no colour at all (or a colour the palette no longer has) stays neutral. */
export const CLASSES_SEM_COR: IClassesCorMarca = {
  fundo: 'bg-muted-active',
  borda: 'border-border-strong',
  texto: 'text-foreground',
  preenche: 'fill-muted-active',
};

export function isCorMarca(valor: string | null | undefined): valor is TCorMarca {
  return typeof valor === 'string' && (CORES_MARCA as ReadonlyArray<string>).includes(valor);
}

export function classesDaCorMarca(cor: string | null | undefined): IClassesCorMarca {
  return isCorMarca(cor) ? CLASSES[cor] : CLASSES_SEM_COR;
}
